<template>
    <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('common.share_image') }}
            </v-card-title>
            <v-row>
                <v-col cols="12">
                    <canvas id="imageCanvas" width="100%" />
                    <fab-menu :items="options" @click:coach="handleCoach" @click:share="handleShare" />
                </v-col>
            </v-row>
        </v-card>
    </v-dialog>
</template>

<script>
import { db, Timestamp, storageRef } from '@/utils/Firebase'
import { mapMutations, mapState } from "vuex";
import FabMenu from './FabMenu.vue';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { randomString } from "@/utils/Common";
import { Device } from "@/utils/Capacitor";

export default {
    components: { FabMenu },
    name: "ShareImageDialog",
    props: {
        image: {
            type: String,
            default: null
        },
        exercise: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            dialog: false,
            options: [
                { icon: 'mdi-share-variant', value: 'share' },
                { icon: 'mdi-account-arrow-left', value: 'coach' }
            ],
            folderPath: 'my_cache_images'
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
    },
    mounted() {
        if (this.image) this.drawImageWithText()
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
        },
        drawImageWithText() {
            const canvas = document.getElementById('imageCanvas');
            if (!canvas) return;
            const ctx = canvas.getContext('2d');

            const img = new Image();
            img.src = this.image;

            img.onload = () => {
                const aspectRatio = img.width / img.height;
                const maxHeight = window.innerHeight * 0.8;
                const maxWidth = window.innerWidth;
                if (img.width > img.height) {
                    canvas.width = Math.min(maxWidth, img.width);
                    canvas.height = canvas.width / aspectRatio;
                } else {
                    canvas.height = Math.min(maxHeight, img.height);
                    canvas.width = canvas.height * aspectRatio;
                }

                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const text = this.exercise.name;
                ctx.font = '16px Arial';
                ctx.fillStyle = 'white';
                ctx.textAlign = 'left';
                ctx.fillText(text, 10, canvas.height - 10);

                const overlayImg = new Image();
                overlayImg.src = this.imgPlaceholder;
                overlayImg.onload = () => {
                    const maxWidth = 60;
                    const maxHeight = 60;
                    const aspectRatioOverlay = overlayImg.width / overlayImg.height;
                    let width, height;
                    if (overlayImg.width > overlayImg.height) {
                        width = Math.min(maxWidth, overlayImg.width);
                        height = width / aspectRatioOverlay;
                    } else {
                        height = Math.min(maxHeight, overlayImg.height);
                        width = height * aspectRatioOverlay;
                    }
                    ctx.drawImage(overlayImg, canvas.width - width - 10, 10, width, height);
                };
            };
        },
        async uploadImage(image) {
            const vm = this;
            let uniqueName = `${Date.now()}-${vm.exercise.name.replace(/\s+/g, '-')}`
            let savePath = `/${vm.studentId}/chats/${vm.coachId}/${uniqueName}`
            let uploadTask = await storageRef.child(savePath).put(image)
            let downloadURL = await uploadTask.ref.getDownloadURL()
            return { savePath, downloadURL }
        },
        async sendMessageToCoach(savePath, downloadURL) {
            const vm = this

            let chatRef = await db.collection('privateChats')
                .where('type', '==', 'coach_student')
                .where('participants', 'in', [[vm.coachId, vm.studentId]])
                .get()

            let chatId = null
            if (chatRef.docs.length > 0) {
                chatId = chatRef.docs[0].id
            } else {
                let newChat = await db.collection('privateChats').add({
                    type: 'coach_student',
                    participants: [vm.coachId, vm.studentId]
                })
                chatId = newChat.id
            }

            let payload = {
                message: vm.exercise.name,
                from: vm.studentId,
                read: false,
                timestamp: Timestamp.now(),
                type: 'attachment',
                file: { savepath: savePath, downloadURL: downloadURL }
            }

            await db.collection('privateChats').doc(chatId).collection('messages').add(payload)
            await db.collection('privateChats').doc(chatId).update({ lastMessage: payload })
        },
        handleCoach() {
            const canvas = document.getElementById('imageCanvas');
            if (!canvas) return;

            canvas.toBlob(async (blob) => {
                if (!blob) return;
                try {
                    const { savePath, downloadURL } = await this.uploadImage(blob)
                    this.sendMessageToCoach(savePath, downloadURL);
                    vm.toast(vm.$t('common.image_coach_sent'))
                    vm.hide()
                } catch (error) {
                    console.error("Error handleCoach:", error);
                }
            }, 'image/png')
        },
        async handleShare() {
            const vm = this;
            const canvas = document.getElementById('imageCanvas');
            if (!canvas) return;

            canvas.toBlob(async (blob) => {
                if (!blob) return;
                try {
                    if (await Device.isWeb()) {
                        const { downloadURL } = await this.uploadImage(blob)
                        await Share.share({
                            title: vm.$t('common.share_image'),
                            text: vm.$t('common.share_image'),
                            url: downloadURL,
                            dialogTitle: vm.$t('common.share_image'),
                        });
                    } else {
                        const dirResult = await Filesystem.readdir({
                            path: vm.folderPath,
                            directory: Directory.Cache,
                        }).catch(() => null);

                        if (!dirResult) {
                            await Filesystem.mkdir({
                                path: vm.folderPath,
                                directory: Directory.Cache,
                                recursive: true,
                            });
                        }

                        const reader = new FileReader();
                        reader.readAsDataURL(blob);
                        reader.onloadend = async () => {
                            const base64Data = reader.result.split(",")[1];

                            const savedFile = await Filesystem.writeFile({
                                path: `${vm.folderPath}/${randomString()}_${vm.exercise.name}.jpg`,
                                data: base64Data,
                                directory: Directory.Cache,
                            });

                            await Share.share({
                                title: vm.$t('common.share_image'),
                                text: vm.$t('common.share_image'),
                                files: [`file://${savedFile.uri}`],
                                dialogTitle: vm.$t('common.share_image'),
                            });
                        }
                    }
                    vm.toast(vm.$t('common.shared_image'));
                    vm.hide();
                } catch (error) {
                    console.error("Error en handleShare:", error);
                }
            }, 'image/png');
        }
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
        image(val) {
            if (val && this.dialog) this.$nextTick(() => this.drawImageWithText());
        }
    }
}
</script>

<style scoped>
.card-container {
    border-radius: 0px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}

#imageCanvas {
    display: block;
    width: 100%;
    height: auto;
}
</style>